import { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet';
import withTranslation from '../../hoc/withTranslation';
import { useLocation } from 'react-router-dom';
import { routeTranslations } from '../../App';
import { Language } from '../../utils';
import { isCategoryProductPath, pageUrls, toCmsUrl } from '../../utils/cmsSeoUrl';
import SeoHead from './SeoHead';

const DOMAIN = process.env.REACT_APP_DOMAIN_URL || '';
const SHARE_IMAGE = `${DOMAIN.replace(/\/$/, '')}/img/logo/logo-header.png`;

const SeoTags = (props: any) => {
  const location = useLocation();

  const currentLang =
    (location.pathname.split('/')[1] as Language) || 'en';

  const routes = routeTranslations[currentLang] || routeTranslations.en;
  const cmsUrl = toCmsUrl(
    location.pathname,
    DOMAIN,
    routes,
    routeTranslations.en
  );

  const isProductPage = isCategoryProductPath(
    location.pathname,
    currentLang,
    routes.categories
  );

  // Only an exact page match. Pages missing from the CMS record stay
  // without these tags, instead of inheriting the homepage title.
  const currentPageData = isProductPage
    ? null
    : props?.translation?.pages?.find(
        (page: { url: string }) =>
          page.url.replace(/\/$/, '') === cmsUrl
      );

  const documentLang = currentLang === 'pl' ? 'pl' : 'en';
  const section = location.pathname.split('/')[2];
  const isPrivatePage =
    section === routes.dashboard ||
    section === routes.shoppingCart ||
    section === routes.drafts;

  // Layout runs before the login redirect, so the tag is on the address
  // even when a visitor without a session is sent back to the homepage.
  useLayoutEffect(() => {
    if (!isPrivatePage) return;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [isPrivatePage]);

  if (!props?.translationLoaded || !currentPageData?.pageTitle) {
    return (
      <Helmet>
        <html lang={documentLang} />
      </Helmet>
    );
  }

  const { canonical, alternates } = pageUrls(
    location.pathname,
    DOMAIN,
    routes,
    routeTranslations.en,
    routeTranslations.pl
  );
  const description = currentPageData.description || '';

  return (
    <>
    <Helmet>
      <html lang={documentLang} />
    </Helmet>
    <SeoHead
      title={currentPageData.pageTitle}
      description={description}
      keywords={props?.translation?.keywords}
      canonical={canonical}
      alternates={alternates}
      image={SHARE_IMAGE}
      locale={currentLang === 'pl' ? 'pl_PL' : 'en_GB'}
      alternateLocale={currentLang === 'pl' ? 'en_GB' : 'pl_PL'}
      type='website'
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: currentPageData.pageTitle,
        ...(description ? { description } : {}),
        url: canonical,
        inLanguage: currentLang === 'pl' ? 'pl' : 'en',
        isPartOf: {
          '@type': 'WebSite',
          name: 'HAWC',
          url: DOMAIN.replace(/\/$/, ''),
        },
      }}
    />
    </>
  );
};

export default withTranslation('seo')(SeoTags);
